import { el, eventInside, iconBtn, type DesignerCtx } from './panels.js';

/**
 * Dialogs and popovers that belong to the designer, not to the page.
 *
 * Both are plain absolutely-positioned nodes inside the designer's own root,
 * never `<dialog>` and never the widget's top-layer helper: the designer is
 * already inside a full-screen dialog, and a second top-layer element would
 * trap focus away from the canvas the shopper is looking at. Keeping them in
 * the root also means the shadow stylesheet reaches them and a shop theme
 * cannot restyle a colour picker.
 *
 * The rules both follow: Escape closes, a click outside closes, focus moves
 * into the card on open and back to the trigger on close, and exactly one of
 * each is open at a time (opening a second closes the first).
 */

export interface OverlayHandle {
  readonly element: HTMLElement;
  close(): void;
  /** True while the node is still on screen. */
  open(): boolean;
}

interface OverlayBase {
  ctx: DesignerCtx;
  /** Extra class on the card, for per-overlay width and padding. */
  className?: string;
  /** Fills the card body; may return a teardown. */
  build(body: HTMLElement, handle: OverlayHandle): void | (() => void);
  onClose?(): void;
}

export interface PopoverOptions extends OverlayBase {
  /** The button the popover hangs under, and what focus returns to. */
  anchor: HTMLElement;
  title?: string;
  /** Prefer opening upwards (bottom-bar buttons do). */
  above?: boolean;
  /** A point instead of the anchor's box: the canvas context menu. */
  at?: { clientX: number; clientY: number };
}

export interface DialogOptions extends OverlayBase {
  title: string;
  /** A footer button row; omit for a dialog that only closes with ✕. */
  footer?(row: HTMLElement, handle: OverlayHandle): void;
}

/** The overlay currently on screen, so a second open replaces the first. */
const openOverlays = new WeakMap<DesignerCtx, OverlayHandle>();

function closeCurrent(ctx: DesignerCtx): void {
  const current = openOverlays.get(ctx);
  if (current?.open()) current.close();
  openOverlays.delete(ctx);
}

/** Closes whatever popover or dialog this designer has open. */
export function closeOverlays(ctx: DesignerCtx): void {
  closeCurrent(ctx);
}

function mount(
  options: OverlayBase,
  card: HTMLElement,
  extras: { backdrop?: HTMLElement; restoreTo?: HTMLElement | null },
): OverlayHandle {
  const ctx = options.ctx;
  let alive = true;
  let teardown: (() => void) | undefined;

  const handle: OverlayHandle = {
    element: card,
    open: () => alive,
    close() {
      if (!alive) return;
      alive = false;
      teardown?.();
      document.removeEventListener('pointerdown', onOutside, true);
      document.removeEventListener('keydown', onKey, true);
      card.remove();
      extras.backdrop?.remove();
      options.onClose?.();
      try {
        extras.restoreTo?.focus({ preventScroll: true });
      } catch {
        /* the trigger may have been rebuilt underneath us */
      }
    },
  };

  const onOutside = (event: PointerEvent): void => {
    // `eventInside`, not `card.contains(event.target)`: the designer lives in
    // a shadow root, and a document-level listener sees the shadow HOST as the
    // target — which would close this card on its own first click.
    if (eventInside(event, card)) return;
    // The anchor's own click would immediately re-open what it just closed.
    if (extras.restoreTo && eventInside(event, extras.restoreTo)) return;
    handle.close();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Tab' && extras.backdrop) {
      const targets = focusable(card);
      const active = activeInside(card);
      const first = targets[0];
      const last = targets[targets.length - 1];
      if (!first || !active || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus({ preventScroll: true });
      }
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    handle.close();
  };

  document.addEventListener('pointerdown', onOutside, true);
  document.addEventListener('keydown', onKey, true);

  const body = card.querySelector('.pzd-over-body') as HTMLElement;
  const result = options.build(body, handle);
  if (typeof result === 'function') teardown = result;

  openOverlays.set(ctx, handle);
  return handle;
}

/** A small card hanging off a toolbar or bottom-bar button. */
export function openPopover(options: PopoverOptions): OverlayHandle {
  closeCurrent(options.ctx);
  const card = el('div', `pzd-popover${options.className ? ` ${options.className}` : ''}`);
  card.setAttribute('role', 'dialog');
  if (options.title) card.setAttribute('aria-label', options.title);
  if (options.title) {
    const head = el('div', 'pzd-over-head');
    head.append(el('h4', undefined, options.title));
    card.append(head);
  }
  card.append(el('div', 'pzd-over-body'));
  options.ctx.root.append(card);

  const handle = mount(options, card, { restoreTo: options.anchor });
  if (options.title) {
    const head = card.querySelector('.pzd-over-head') as HTMLElement;
    head.append(iconBtn('close', options.ctx.texts.close, () => handle.close(), { className: 'pzd-icon-btn is-plain' }));
  }
  place(card, options.anchor, options.ctx.root, options.above === true, options.at);
  focusFirst(card);
  return handle;
}

/** A centred card with a backdrop: settings, shortcuts, the font picker, QR. */
export function openDialog(options: DialogOptions): OverlayHandle {
  closeCurrent(options.ctx);
  const restoreTo = activeInside(options.ctx.root);
  const backdrop = el('div', 'pzd-backdrop');
  const card = el('div', `pzd-dialog${options.className ? ` ${options.className}` : ''}`);
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-label', options.title);
  const head = el('div', 'pzd-over-head');
  head.append(el('h4', undefined, options.title));
  card.append(head, el('div', 'pzd-over-body'));
  options.ctx.root.append(backdrop, card);

  const handle = mount(options, card, { backdrop, restoreTo });
  head.append(iconBtn('close', options.ctx.texts.close, () => handle.close(), { className: 'pzd-icon-btn is-plain' }));
  if (options.footer) {
    const row = el('div', 'pzd-over-foot');
    options.footer(row, handle);
    card.append(row);
  }
  focusFirst(card);
  return handle;
}

/**
 * Hangs the card under (or over) its anchor and then pulls it back inside the
 * designer's box. A popover that opened off the right edge of a 1024 px laptop
 * is a popover the shopper cannot use.
 */
function place(
  card: HTMLElement,
  anchor: HTMLElement,
  root: HTMLElement,
  above: boolean,
  at?: { clientX: number; clientY: number },
): void {
  const anchorRect = anchor.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const margin = 8;
  let left = at ? at.clientX - rootRect.left : anchorRect.left - rootRect.left;
  let top = at
    ? at.clientY - rootRect.top
    : above
      ? anchorRect.top - rootRect.top - cardRect.height - 6
      : anchorRect.bottom - rootRect.top + 6;
  left = Math.max(margin, Math.min(left, rootRect.width - cardRect.width - margin));
  top = Math.max(margin, Math.min(top, Math.max(margin, rootRect.height - cardRect.height - margin)));
  card.style.left = `${Math.round(left)}px`;
  card.style.top = `${Math.round(top)}px`;
}

function focusable(card: HTMLElement): HTMLElement[] {
  return [...card.querySelectorAll<HTMLElement>(
    'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
  )].filter(node => node.tabIndex >= 0 && node.getClientRects().length > 0);
}

function focusFirst(card: HTMLElement): void {
  const targets = focusable(card);
  const target = targets.find(node => node.closest('.pzd-over-body')) ?? targets[0];
  try {
    target?.focus({ preventScroll: true });
  } catch {
    /* older engines take no options */
  }
}

function activeInside(root: HTMLElement): HTMLElement | null {
  const owner = root.getRootNode();
  const active = owner instanceof ShadowRoot ? owner.activeElement : document.activeElement;
  return active instanceof HTMLElement && root.contains(active) ? active : null;
}
